import * as React from 'react';
import { useCallback, useEffect } from 'react';
import { ThemeProvider } from '@fluentui/react';
import clsx from 'clsx';
import { useAppTheme, useCurrentThemeType } from 'common/theme';
import { pageDataService } from 'common/utilities';
import { getCookie } from 'common/utilities/Cookie';
import { PerformanceBar } from 'common/utilities/PerformanceBar';
import { useLocalizedStrings } from 'i18n';
import { useAppSelector } from 'store';

import { getStyles } from './Layout.styles';
import { OfficeShell } from './OfficeShell';

interface ILayoutProps {
  children?: React.ReactNode;
  classNames?: {
    shell?: string;
    viewport?: string;
  };
}

export const layout_wrapper_id = 'layout-wrapper';
export const main_content_id = 'main-content';

const Layout: React.FC<ILayoutProps> = (props: ILayoutProps) => {
  const { t } = useLocalizedStrings();
  const { children, classNames } = props;

  const shouldRenderOfficeShell = !pageDataService.isWebView();
  const { screenSize, screenMode, isBlurryBackground, useCustomScrollBar } = useAppSelector((state) => state.layout);
  const theme = useAppTheme();
  const themeName = useCurrentThemeType();
  const styles = getStyles(theme, themeName, screenSize, screenMode, isBlurryBackground);

  const renderPerfBar = (): JSX.Element | null => {
    const isPerfBarEnabled = getCookie('PerfBar') === 'enabled';

    return isPerfBarEnabled ? <PerformanceBar /> : null;
  };

  useEffect(() => {
    if (process.env.NODE_ENV !== 'test') {
      // eslint-disable-next-line no-console
      console.log(`%c${t('DeveloperConsoleWarning.heading')}`, 'color: red; font-size: 36px; font-weight: bold;');
      // eslint-disable-next-line no-console
      console.log(`%c${t('DeveloperConsoleWarning.subheading')}`, 'font-size: 24px; font-weight: bold;');
    }
  }, [t]);

  const handleSkipToMain = useCallback((event: React.KeyboardEvent<HTMLAnchorElement> | React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const mainContent = document.getElementById(main_content_id);
    if (mainContent) {
      // Find the first focusable element within main content
      const focusableElements = mainContent.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstFocusableElement = focusableElements[0] as HTMLElement;

      if (firstFocusableElement) {
        firstFocusableElement.focus();
      } else {
        // If no focusable element, focus the main element itself
        mainContent.focus();
      }
    }
  }, []);

  return (
    <ThemeProvider theme={theme} className={clsx(styles.bodyStyle, useCustomScrollBar && styles.customScrollBarStyle)}>
      <a
        href={`#${main_content_id}`}
        className={styles.skipLink}
        onClick={handleSkipToMain}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleSkipToMain(e);
          }
        }}
      >
        {/* eslint-disable i18next/no-literal-string */}
        Skip to main content
        {/* eslint-enable i18next/no-literal-string */}
      </a>
      {shouldRenderOfficeShell && <OfficeShell />}
      <div id={layout_wrapper_id} className={clsx(classNames?.shell, styles.shell)}>
        {/* eslint-disable-next-line i18next/no-literal-string */}
        <main
          id={main_content_id}
          className={clsx(classNames?.viewport, styles.viewport)}
          tabIndex={-1}
          role="main"
          aria-label="Main content"
        >
          {children}
        </main>
      </div>
      {renderPerfBar()}
    </ThemeProvider>
  );
};

export default Layout;
